import { useMemo } from 'react';
import { useAuth, useAuthActions } from '../state/AuthContext';
import { buildAchievements, getAchievementStats } from '../utils/achievements';
import '../styles/achievements.css';

// Per-category badge artwork. Each is a small inline SVG so it inherits the
// theme the same way the rest of the app's icons do.
function CategoryIcon({ category }) {
  switch (category) {
    case 'discovered':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="26" height="26" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <polygon points="16.2 7.8 13.5 13.5 7.8 16.2 10.5 10.5" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'favorited':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26" aria-hidden="true">
          <path d="M12 21s-7.2-4.6-9.6-9C1.1 9.3 2.3 6 5.4 6c1.9 0 3.2 1.1 4.1 2.3C10.4 7.1 11.7 6 13.6 6c3.1 0 4.3 3.3 3 6-2.4 4.4-9.6 9-9.6 9z" transform="translate(1.2 0)" />
        </svg>
      );
    case 'smallArtists':
      // A cut gem — the "hidden gem" / underground motif.
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" width="26" height="26" aria-hidden="true">
          <path d="M6 3h12l3 5-9 13L3 8z" />
          <path d="M3 8h18" />
          <path d="M9 3 7.5 8 12 21 16.5 8 15 3" />
        </svg>
      );
    case 'friends':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="26" height="26" aria-hidden="true">
          <path d="M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20" />
          <circle cx="10" cy="8" r="3.2" />
          <path d="M20 20v-1.5a3.5 3.5 0 0 0-2.6-3.4" />
          <path d="M15.5 5.2a3.2 3.2 0 0 1 0 6" />
        </svg>
      );
    case 'genre':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="26" height="26" aria-hidden="true">
          <circle cx="7" cy="17.5" r="2.6" />
          <circle cx="17.5" cy="15" r="2.6" />
          <path d="M9.6 17.5V6.5l10.5-2.2v10.7" />
        </svg>
      );
    case 'explored':
    default:
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26" aria-hidden="true">
          <path d="M12 2.5l2.6 6 6.5.5-5 4.2 1.6 6.3L12 16.2 6.3 19.5l1.6-6.3-5-4.2 6.5-.5z" />
        </svg>
      );
  }
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="16" height="16" aria-hidden="true">
      <rect x="4.5" y="11" width="15" height="9" rx="2" />
      <path d="M8 11V7.5a4 4 0 0 1 8 0V11" />
    </svg>
  );
}

function AchievementCard({ achievement }) {
  const { category, title, description, threshold, current, unlocked, progress, color } = achievement;
  // Genre badges carry their own accent color; tint the icon when unlocked.
  const iconStyle = unlocked && color ? { color } : undefined;
  return (
    <div
      className={`achievement-card achievement-${category}${unlocked ? ' unlocked' : ' locked'}`}
      role="listitem"
      aria-label={`${title} — ${unlocked ? 'unlocked' : 'locked'}: ${description}`}
    >
      <div className="achievement-icon" style={iconStyle}>
        <CategoryIcon category={category} />
        {!unlocked && (
          <span className="achievement-lock" aria-hidden="true">
            <LockIcon />
          </span>
        )}
      </div>
      <div className="achievement-meta">
        <span className="achievement-title">{title}</span>
        <span className="achievement-desc">{description}</span>
      </div>
      {unlocked ? (
        <span className="achievement-status unlocked">Unlocked</span>
      ) : (
        <span className="achievement-status locked">
          <span className="achievement-progress-track" aria-hidden="true">
            <span className="achievement-progress-fill" style={{ width: `${Math.round(progress * 100)}%` }} />
          </span>
          <span className="achievement-progress-text">
            {Math.min(current, threshold).toLocaleString()} / {threshold.toLocaleString()}
          </span>
        </span>
      )}
    </div>
  );
}

function AchievementsSection() {
  const auth = useAuth();
  const { user, isLoading } = auth;
  const { showAuthModal } = useAuthActions();

  const { groups, genreGroups, genresPending, unlockedCount, total } = useMemo(
    () => buildAchievements(getAchievementStats(auth)),
    [auth]
  );

  if (!user && !isLoading) {
    return (
      <div className="achievements-section">
        <div className="achievements-signin">
          <span className="achievements-signin-text">
            Sign in to start earning achievements as you explore.
          </span>
          <button className="achievements-signin-btn" onClick={() => showAuthModal('register')}>
            Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="achievements-section">
      <div className="achievements-summary">
        <span className="achievements-summary-count">{unlockedCount}</span>
        <span className="achievements-summary-label">of {total} unlocked</span>
      </div>

      {groups.map((group) => (
        <div className="achievement-group" key={group.category}>
          <h4 className="achievement-group-header">
            {group.label}
            <span className="section-count">
              {group.achievements.filter((a) => a.unlocked).length}/{group.achievements.length}
            </span>
          </h4>
          <div className="achievement-grid" role="list">
            {group.achievements.map((a) => (
              <AchievementCard key={a.id} achievement={a} />
            ))}
          </div>
        </div>
      ))}

      {/* Genre achievements — counts come from the user's computed universe */}
      <div className="achievement-genre-intro">
        <span className="achievement-genre-intro-title">Genres</span>
        {genresPending && (
          <span className="achievement-genre-intro-hint">
            Build your universe to start tracking the genres you explore.
          </span>
        )}
      </div>

      {genreGroups.map((group) => (
        <div className="achievement-group" key={group.genreKey}>
          <h4 className="achievement-group-header">
            <span className="achievement-genre-dot" style={{ background: group.color }} aria-hidden="true" />
            {group.label}
            <span className="section-count">
              {group.achievements.filter((a) => a.unlocked).length}/{group.achievements.length}
            </span>
          </h4>
          <div className="achievement-grid" role="list">
            {group.achievements.map((a) => (
              <AchievementCard key={a.id} achievement={a} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default AchievementsSection;
